// ServiceMasterTab.tsx — Admin Panel -> Insurance Claims -> Service Master.
// The chargeable-service master (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.3,
// Insurance Claims Phase 2.1/4.5.7, wireframe S9's second card) — code,
// service name, claim head, rate, unit and the non-payable flag a charge
// picks up when a staff member selects it on the IP charges screen.
//
// CSV import/export (design §7, plan 2.1, wireframe S9) is the shared
// MasterCsvImportBar — create-only, same rule as the Payers tab.
import { useState, useEffect, useCallback } from 'react';
import { MasterCsvImportBar } from './MasterCsvImportBar';
import { getServiceMaster, createServiceMaster, updateServiceMaster, deleteServiceMaster } from '../api';
import { CLAIM_HEADS, type ServiceMasterItem } from '../types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { LoadingState } from '@/components/ui/States';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';

const UNITS = ['each', 'day', 'hour', 'visit', 'session'] as const;

type Draft = {
  code: string; name: string; department: string; claim_head: string; unit: string;
  default_rate: string; gst_percent: string; sac_hsn: string; non_payable_default: boolean;
  auto_daily_for_ward_types: string;
};

const BLANK_DRAFT: Draft = {
  code: '', name: '', department: '', claim_head: 'misc', unit: 'each',
  default_rate: '0', gst_percent: '0', sac_hsn: '', non_payable_default: false,
  auto_daily_for_ward_types: '',
};

function toDraft(s: ServiceMasterItem): Draft {
  return {
    code: s.code, name: s.name, department: s.department ?? '', claim_head: s.claim_head, unit: s.unit,
    default_rate: String(s.default_rate), gst_percent: String(s.gst_percent), sac_hsn: s.sac_hsn ?? '',
    non_payable_default: s.non_payable_default,
    auto_daily_for_ward_types: (s.auto_daily_for_ward_types ?? []).join(', '),
  };
}

/** Comma-separated ward types -> the TEXT[] the server expects, or
 * undefined for an empty field (so it's dropped from the payload rather
 * than sent as an empty array that would clear a value the caller didn't
 * touch — matches the API's own "field omitted = unchanged" convention). */
function parseWardTypes(s: string): string[] | null {
  const list = s.split(',').map((t) => t.trim()).filter(Boolean);
  return list.length ? list : null;
}

export default function ServiceMasterTab() {
  const [items, setItems] = useState<ServiceMasterItem[] | null>(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>(BLANK_DRAFT);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Draft | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    getServiceMaster({ active: 'all' }).then(setItems).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!draft.code.trim() || !draft.name.trim()) return;
    setSaving(true); setError('');
    try {
      await createServiceMaster({
        code: draft.code.trim(), name: draft.name.trim(), department: draft.department.trim() || undefined,
        claim_head: draft.claim_head as ServiceMasterItem['claim_head'], unit: draft.unit as ServiceMasterItem['unit'],
        default_rate: Number(draft.default_rate) || 0, gst_percent: Number(draft.gst_percent) || 0,
        sac_hsn: draft.sac_hsn.trim() || undefined, non_payable_default: draft.non_payable_default,
        auto_daily_for_ward_types: parseWardTypes(draft.auto_daily_for_ward_types) ?? undefined,
      });
      setDraft(BLANK_DRAFT);
      setAdding(false);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  const startEdit = (s: ServiceMasterItem) => { setEditingId(s.id); setEditDraft(toDraft(s)); };

  const saveEdit = async () => {
    if (!editingId || !editDraft) return;
    setError('');
    try {
      await updateServiceMaster(editingId, {
        name: editDraft.name.trim(), department: editDraft.department.trim() || null,
        claim_head: editDraft.claim_head as ServiceMasterItem['claim_head'], unit: editDraft.unit as ServiceMasterItem['unit'],
        default_rate: Number(editDraft.default_rate) || 0, gst_percent: Number(editDraft.gst_percent) || 0,
        sac_hsn: editDraft.sac_hsn.trim() || null, non_payable_default: editDraft.non_payable_default,
        auto_daily_for_ward_types: parseWardTypes(editDraft.auto_daily_for_ward_types),
      });
      setEditingId(null);
      setEditDraft(null);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const toggleActive = async (s: ServiceMasterItem) => {
    setError('');
    try { await updateServiceMaster(s.id, { active: !s.active }); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const remove = async (s: ServiceMasterItem) => {
    setError('');
    try { await deleteServiceMaster(s.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  if (!items) return <LoadingState label="Loading service master…" />;

  const visible = items
    .filter((s) => showInactive || s.active)
    .filter((s) => !q.trim() || `${s.code} ${s.name}`.toLowerCase().includes(q.trim().toLowerCase()));

  // Wireframe S9's dense Service master table (Code/Service/Claim head/
  // Rate/Unit/NP).
  const serviceColumns: DataTableColumn<ServiceMasterItem>[] = [
    { key: 'code', header: 'Code', render: (s) => <span className="font-medium">{s.code}</span> },
    {
      key: 'name', header: 'Service', sortable: true, sortValue: (s) => s.name,
      render: (s) => <>{s.name}{!s.active && <Badge variant="neutral" className="ml-2">Inactive</Badge>}</>,
    },
    { key: 'head', header: 'Claim head', render: (s) => s.claim_head },
    { key: 'rate', header: 'Rate', align: 'right', render: (s) => `₹${Number(s.default_rate).toFixed(2)}` },
    { key: 'unit', header: 'Unit', render: (s) => s.unit },
    { key: 'np', header: 'NP', align: 'center', render: (s) => s.non_payable_default ? '✔' : '' },
    {
      key: 'actions', header: '', align: 'right',
      render: (s) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => startEdit(s)}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => toggleActive(s)}>{s.active ? 'Deactivate' : 'Reactivate'}</Button>
          <Button size="sm" variant="danger" onClick={() => remove(s)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="fade-in">
      <div className="mb-3 text-xs text-ink-muted">
        The chargeable-service catalogue — code, name, claim head, rate and unit. Picking one of these on the
        IP charges screen (or on a service's own ward-type list, for the optional auto-daily nursing/doctor-visit
        charges) sets the line&apos;s claim head automatically, rather than leaving it to be guessed from a free-text
        description.
      </div>

      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      <MasterCsvImportBar master="service-master" onImported={load} />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or name…" className="w-56" />
          <label className="flex items-center gap-1.5 text-xs text-ink-muted">
            <Checkbox checked={showInactive} onCheckedChange={(v) => setShowInactive(!!v)} />
            Show inactive
          </label>
        </div>
        {!adding && <Button size="sm" onClick={() => setAdding(true)}>+ Add service</Button>}
      </div>

      {adding && (
        <Card className="mb-4 p-4">
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
            <FormField label="Code" required>
              <Input value={draft.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))} placeholder="e.g. NUR-ICU" />
            </FormField>
            <FormField label="Name" required className="col-span-2">
              <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. ICU nursing care" />
            </FormField>
            <FormField label="Department">
              <Input value={draft.department} onChange={(e) => setDraft((d) => ({ ...d, department: e.target.value }))} />
            </FormField>
            <FormField label="Claim head" required>
              <NativeSelect value={draft.claim_head} onChange={(e) => setDraft((d) => ({ ...d, claim_head: e.target.value }))}>
                {CLAIM_HEADS.map((h) => <option key={h} value={h}>{h}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Unit">
              <NativeSelect value={draft.unit} onChange={(e) => setDraft((d) => ({ ...d, unit: e.target.value }))}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Default rate ₹">
              <Input type="number" min={0} value={draft.default_rate} onChange={(e) => setDraft((d) => ({ ...d, default_rate: e.target.value }))} />
            </FormField>
            <FormField label="GST %">
              <Input type="number" min={0} max={100} value={draft.gst_percent} onChange={(e) => setDraft((d) => ({ ...d, gst_percent: e.target.value }))} />
            </FormField>
            <FormField label="SAC/HSN">
              <Input value={draft.sac_hsn} onChange={(e) => setDraft((d) => ({ ...d, sac_hsn: e.target.value }))} />
            </FormField>
            <FormField label="Auto-daily for ward types (comma-separated)" className="col-span-2">
              <Input value={draft.auto_daily_for_ward_types} onChange={(e) => setDraft((d) => ({ ...d, auto_daily_for_ward_types: e.target.value }))} placeholder="ICU, HDU" />
            </FormField>
          </div>
          <label className="mb-3 flex items-center gap-1.5 text-xs">
            <Checkbox checked={draft.non_payable_default} onCheckedChange={(v) => setDraft((d) => ({ ...d, non_payable_default: !!v }))} />
            Non-payable by default (IRDAI non-payable list)
          </label>
          <div className="flex gap-2">
            <Button size="sm" disabled={saving || !draft.code.trim() || !draft.name.trim()} loading={saving} onClick={submit}>
              Save service
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setDraft(BLANK_DRAFT); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {editingId != null && editDraft && (
        <Card className="mb-4 p-4">
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
            <FormField label="Code"><Input value={editDraft.code} disabled /></FormField>
            <FormField label="Name" className="col-span-2">
              <Input value={editDraft.name} onChange={(e) => setEditDraft((d) => d && ({ ...d, name: e.target.value }))} />
            </FormField>
            <FormField label="Department">
              <Input value={editDraft.department} onChange={(e) => setEditDraft((d) => d && ({ ...d, department: e.target.value }))} />
            </FormField>
            <FormField label="Claim head">
              <NativeSelect value={editDraft.claim_head} onChange={(e) => setEditDraft((d) => d && ({ ...d, claim_head: e.target.value }))}>
                {CLAIM_HEADS.map((h) => <option key={h} value={h}>{h}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Unit">
              <NativeSelect value={editDraft.unit} onChange={(e) => setEditDraft((d) => d && ({ ...d, unit: e.target.value }))}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Default rate ₹">
              <Input type="number" min={0} value={editDraft.default_rate} onChange={(e) => setEditDraft((d) => d && ({ ...d, default_rate: e.target.value }))} />
            </FormField>
            <FormField label="GST %">
              <Input type="number" min={0} max={100} value={editDraft.gst_percent} onChange={(e) => setEditDraft((d) => d && ({ ...d, gst_percent: e.target.value }))} />
            </FormField>
            <FormField label="SAC/HSN">
              <Input value={editDraft.sac_hsn} onChange={(e) => setEditDraft((d) => d && ({ ...d, sac_hsn: e.target.value }))} />
            </FormField>
            <FormField label="Auto-daily for ward types (comma-separated)" className="col-span-2">
              <Input value={editDraft.auto_daily_for_ward_types} onChange={(e) => setEditDraft((d) => d && ({ ...d, auto_daily_for_ward_types: e.target.value }))} />
            </FormField>
          </div>
          <label className="mb-3 flex items-center gap-1.5 text-xs">
            <Checkbox checked={editDraft.non_payable_default} onCheckedChange={(v) => setEditDraft((d) => d && ({ ...d, non_payable_default: !!v }))} />
            Non-payable by default
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveEdit}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditDraft(null); }}>Cancel</Button>
          </div>
        </Card>
      )}

      <DataTable
        columns={serviceColumns}
        rows={visible}
        rowKey={(s) => s.id}
        rowClassName={(s) => (s.active ? undefined : 'opacity-50')}
        emptyLabel="No services yet"
        emptyHint="Add one above."
      />
    </div>
  );
}
